/**
 * Utility functions for table calculations (totals, sums, etc.)
 * Used by PropertyAvailabilities component for nested table calculations
 */

/**
 * Field definition interface - can be either a template column or a sub-entity field
 */
interface FieldDefinition {
  field: string;
  status?: string;
  [key: string]: any;
}

/**
 * Calculate totals for numeric fields in a dataset
 *
 * @param rows - Array of data rows to calculate totals from
 * @param fields - Array of field definitions (columns)
 * @param filterByPropertyType - Whether to filter rows by property type
 * @param propertyTypeValue - The property type value to filter by (e.g., "Retail", "Office")
 * @returns Object with field names as keys and total sums as values
 */
export const calculateFieldTotals = (
  rows: any[],
  fields: FieldDefinition[],
  filterByPropertyType: boolean,
  propertyTypeValue?: string,
): { [key: string]: number } => {
  const totals: { [key: string]: number } = {};

  if (!rows || rows.length === 0 || !fields || fields.length === 0) {
    return totals;
  }

  // Find space type field once for efficiency
  const spaceTypeField = fields.find((field: FieldDefinition) => {
    const fieldLower = (field.field || '').toLowerCase();
    return fieldLower.includes('space_type') || fieldLower.includes('spacetype');
  })?.field;

  // Find numeric fields (size, area, SF, sqft, etc.)
  const numericFields = fields
    .filter((field: FieldDefinition) => field.status === 'ACTIVE' || !field.status)
    .filter((field: FieldDefinition) => {
      const fieldLower = (field.field || '').toLowerCase();
      return (
        fieldLower.includes('size') ||
        fieldLower.includes('area') ||
        fieldLower.includes('sf') ||
        fieldLower.includes('sqft')
      );
    });

  // Calculate sums for each numeric field
  numericFields.forEach((field: FieldDefinition) => {
    const fieldKey = field.field;
    let sum = 0;

    rows.forEach((row: any) => {
      // Filter by property type if required
      if (filterByPropertyType && propertyTypeValue && spaceTypeField) {
        const spaceType = row[spaceTypeField];
        if (!spaceType || !String(spaceType).toLowerCase().includes(propertyTypeValue.toLowerCase())) {
          return; // Skip this row
        }
      }

      const value = row[fieldKey];
      if (value) {
        // Extract numeric value (handle formats like "1 Ha", "12 Ha", "1,000 SF", etc.)
        const numericValue = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
        if (!Number.isNaN(numericValue)) {
          sum += numericValue;
        }
      }
    });

    totals[fieldKey] = sum;
  });

  return totals;
};

/**
 * Calculate totals across multiple sub-entities (nested tables)
 * This is specifically for the nested table structure where data is grouped by entity type
 *
 * @param subEntityData - Array of sub-entity data objects
 * @param filterByPropertyType - Whether to filter rows by property type
 * @param propertyTypeValue - The property type value to filter by
 * @returns Object with field names as keys and total sums as values
 */
export const calculateSubEntityTotals = (
  subEntityData: any[],
  filterByPropertyType: boolean,
  propertyTypeValue?: string,
): { [key: string]: number } => {
  const totals: { [key: string]: number } = {};

  if (!subEntityData || subEntityData.length === 0) {
    return totals;
  }

  // Process each sub-entity (e.g., floor_specifications, suite_details, etc.)
  subEntityData.forEach((entityData: any) => {
    const { subEntityFields, data } = entityData;

    if (!subEntityFields || !data) {
      return;
    }

    // Calculate totals for this sub-entity
    const entityTotals = calculateFieldTotals(
      data,
      subEntityFields,
      filterByPropertyType,
      propertyTypeValue,
    );

    // Accumulate totals across all sub-entities
    Object.keys(entityTotals).forEach((fieldKey) => {
      totals[fieldKey] = (totals[fieldKey] || 0) + entityTotals[fieldKey];
    });
  });

  return totals;
};

/**
 * Extract unit from a formatted value string
 * E.g., "1,234.56 SF" -> "SF", "12 Ha" -> "Ha"
 *
 * @param value - The formatted value string
 * @returns The unit string (trimmed)
 */
export const extractUnit = (value: string | number | undefined): string => {
  if (!value) return '';
  return String(value).replace(/[0-9.,\s-]/g, '').trim();
};

/**
 * Format a total value with its unit
 * E.g., 1234.56, "SF" -> "1234.56 SF"
 *
 * @param total - The numeric total value
 * @param unit - The unit string
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns Formatted string
 */
export const formatTotalWithUnit = (
  total: number,
  unit: string,
  decimalPlaces: number = 2,
): string => `${total.toFixed(decimalPlaces)} ${unit}`.trim();

/**
 * Check if a field is numeric based on its field key
 *
 * @param fieldKey - The field key to check
 * @returns True if the field is likely numeric (size, area, sf, sqft)
 */
export const isNumericField = (fieldKey: string): boolean => {
  const fieldLower = fieldKey.toLowerCase();
  return (
    fieldLower.includes('size') ||
    fieldLower.includes('area') ||
    fieldLower.includes('sf') ||
    fieldLower.includes('sqft')
  );
};

/**
 * Format a numeric value with comma separators and conditional decimals
 * Preserves the unit from the original value
 * E.g., "1234 SF" -> "1,234 SF", "1234.56 Ha" -> "1,234.56 Ha"
 *
 * @param value - The value to format (can be string, number, or undefined)
 * @param fieldKey - The field key to determine if this is a numeric field
 * @returns Formatted value string with comma separators and conditional decimals
 */
export const formatNumericValue = (
  value: string | number | undefined,
  fieldKey: string,
): string => {
  if (!value || value === '-') {
    return '-';
  }

  // Check if this is a numeric field
  if (!isNumericField(fieldKey)) {
    return String(value);
  }

  // Extract numeric part and unit
  const numericValue = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  const unit = extractUnit(value);
  const isInteger = Number.isInteger(numericValue);
  const formattedNumber = numericValue.toLocaleString('en-US', {
    minimumFractionDigits: isInteger ? 0 : 2,
    maximumFractionDigits: 2,
  });

  return `${formattedNumber} ${unit}`.trim();
};
